/**
 * Role-Based Access Control (RBAC) Configuration
 * Defines permissions and accessible routes for each user role
 */

export const ROLES = {
  ADMIN: 'admin',
  COLLEGE_ADMIN: 'college_admin',
  STUDENT: 'student',
  RECRUITER: 'recruiter',
  SUPPORT: 'support',
  OFFICE_ADMIN: 'office_admin',
};

export const ROLE_DASHBOARDS = {
  [ROLES.ADMIN]: '/admin/dashboard',
  [ROLES.COLLEGE_ADMIN]: '/college/dashboard',
  [ROLES.STUDENT]: 'http://localhost:3000/student/dashboard',
  [ROLES.RECRUITER]: '/recruiter/dashboard',
  [ROLES.SUPPORT]: '/support/dashboard',
  [ROLES.OFFICE_ADMIN]: '/admin/dashboard',
};

export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    name: 'Administrator',
    description: 'Full system access and control',
    canAccess: [
      '/dashboard',
      '/admin/dashboard',
      '/colleges',
      '/colleges/onboard',
      '/colleges/edit/:id',
      '/colleges/:id',
      '/users',
      '/courses',
      '/courses/create',
      '/courses/edit/:id',
      '/assessments',
      '/assessments/manage',
      '/assessments/edit/:id',
      '/assessments/view/:id',
      '/analytics',
      '/college/student-progress',
      '/admin/community',
      '/it-support',
      '/support',
      '/support/dashboard',
      '/support/:id',
      '/support/tickets/:id',
      '/my-it-support',
    ],
    features: [
      'Manage all institutions',
      'Manage all users',
      'Create and manage courses',
      'Create and manage assessments',
      'View all analytics',
      'Full system configuration',
    ],
  },
  
  [ROLES.OFFICE_ADMIN]: {
    name: 'Office Administrator',
    description: 'Administrative office access',
    canAccess: [
      '/dashboard',
      '/admin/dashboard',
      '/colleges',
      '/colleges/onboard',
      '/colleges/edit/:id',
      '/colleges/:id',
      '/users',
      '/courses',
      '/courses/create',
      '/courses/edit/:id',
      '/assessments',
      '/assessments/manage',
      '/assessments/edit/:id',
      '/assessments/view/:id',
      '/analytics',
      '/college/student-progress',
    ],
    features: [
      'Manage all institutions',
      'Manage all users',
      'Create and manage courses',
      'Create and manage assessments',
      'View all analytics',
      'Full system configuration',
    ],
  },

  [ROLES.SUPPORT]: {
    name: 'IT Support Agent',
    description: 'Manage support tickets and service requests',
    canAccess: [
      '/dashboard',
      '/support',
      '/support/dashboard',
      '/support/:id',
      '/support/tickets/:id',
      '/my-it-support',
      '/settings',
    ],
    features: [
      'Manage support tickets',
      'Assign tickets to staff',
      'Configure support settings',
    ],
  },
  
  [ROLES.COLLEGE_ADMIN]: {
    name: 'Institute Administrator',
    description: 'Manage students and courses within institution',
    canAccess: [
      '/dashboard',
      '/college/dashboard',
      '/colleges/:id',
      '/courses',
      '/analytics',
      '/college/departments',
      '/college/students',
      '/college/placement',
      '/college/analytics',
      '/college/calendar',
      '/college/reports',
      '/college/notifications',
      '/college/student-progress',
      '/college/students/add',
      '/college/students/bulk-upload',
      '/college/students/:id',
      '/college/courses',
      '/college/degrees',
      '/admin/community',
    ],
    features: [
      'Manage students in institution',
      'Manage local courses',
      'View institution analytics',
      'Monitor student progress',
    ],
  },
  
  [ROLES.STUDENT]: {
    name: 'Student',
    description: 'Redirected to external student portal',
    canAccess: [], // No access to admin dashboard
    features: [
      'Redirected to external student portal',
    ],
  },
  
  [ROLES.RECRUITER]: {
    name: 'Recruiter',
    description: 'Corporate hiring partner portal access',
    canAccess: [
      '/dashboard',
      '/recruiter/dashboard',
      '/recruiter/jobs',
      '/recruiter/applicants',
      '/settings',
    ],
    features: [
      'Manage corporate jobs',
      'View and process candidate applications',
      'Manage profile and settings',
    ],
  },
};

/**
 * Check if a user has permission to access a specific route
 * @param {string} userRole - The role of the user
 * @param {string} route - The route to check
 * @returns {boolean} - Whether the user can access the route
 */
export const canAccessRoute = (userRole, route) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }

  const permissions = ROLE_PERMISSIONS[userRole];
  
  // Check exact match
  if (permissions.canAccess.includes(route)) {
    return true;
  }

  // Check pattern match (for dynamic routes like /institutes/:id)
  return permissions.canAccess.some(allowedRoute => {
    if (allowedRoute.includes(':')) {
      const pattern = allowedRoute.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(route);
    }
    return false;
  });
};

/**
 * Get the default dashboard route for a user role
 * @param {string} userRole - The role of the user
 * @returns {string} - The dashboard route
 */
export const getRoleDashboard = (userRole) => {
  return ROLE_DASHBOARDS[userRole] || '/dashboard';
};

/**
 * Get role display name
 * @param {string} userRole - The role of the user
 * @returns {string} - The display name
 */
export const getRoleName = (userRole) => {
  return ROLE_PERMISSIONS[userRole]?.name || 'Unknown Role';
};

/**
 * Get role description
 * @param {string} userRole - The role of the user
 * @returns {string} - The description
 */
export const getRoleDescription = (userRole) => {
  return ROLE_PERMISSIONS[userRole]?.description || '';
};

/**
 * Get role features
 * @param {string} userRole - The role of the user
 * @returns {array} - The list of features
 */
export const getRoleFeatures = (userRole) => {
  return ROLE_PERMISSIONS[userRole]?.features || [];
};

/**
 * Check if user is admin
 * @param {string} userRole - The role of the user
 * @returns {boolean}
 */
export const isAdmin = (userRole) => {
  return userRole === ROLES.ADMIN || userRole === ROLES.OFFICE_ADMIN;
};

/**
 * Check if user is institute admin
 * @param {string} userRole - The role of the user
 * @returns {boolean}
 */
export const isCollegeAdmin = (userRole) => {
  return userRole === ROLES.COLLEGE_ADMIN;
};

/**
 * Check if user is student
 * @param {string} userRole - The role of the user
 * @returns {boolean}
 */
export const isStudent = (userRole) => {
  return userRole === ROLES.STUDENT;
};

/**
 * Check if user is recruiter
 * @param {string} userRole - The role of the user
 * @returns {boolean}
 */
export const isRecruiter = (userRole) => {
  return userRole === ROLES.RECRUITER;
};

/**
 * Check if user is support
 * @param {string} userRole - The role of the user
 * @returns {boolean}
 */
export const isSupport = (userRole) => {
  return userRole === ROLES.SUPPORT;
};

export default {
  ROLES,
  ROLE_DASHBOARDS,
  ROLE_PERMISSIONS,
  canAccessRoute,
  getRoleDashboard,
  getRoleName,
  getRoleDescription,
  getRoleFeatures,
  isAdmin,
  isCollegeAdmin,
  isStudent,
  isRecruiter,
  isSupport,
};
